import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { useProject } from '../../shared/context/ProjectContext';
import { getSites } from '../inventory/api/inventoryApi';
import { getDisposalTools, getDisposalStats, searchDisposalReconTools } from './api/disposalApi';
import { MIN_QUERY_LENGTH } from '../../shared/searchFields';
import DisposalToolList from './DisposalToolList';
import DisposalDetailPanel from './DisposalDetailPanel';
import DisposalRecycleBin from './DisposalRecycleBin';
import GridView from '../../components/GridView/GridView';
import GridModeToggle from '../../components/GridView/GridModeToggle';
import { useGridState, sortRecords } from '../../components/GridView/useGridState';
import { DISPOSAL_COLUMNS, DISPOSAL_CAMEL_TO_SNAKE } from '../../components/GridView/disposalColumnManifest';
import { useLeftPanelCollapse } from '../../shared/hooks/useLeftPanelCollapse';
import LeftPanelCollapseToggle from '../../shared/components/LeftPanelCollapseToggle';
import { useScrollToTopOnMount } from '../../shared/hooks/useScrollToTopOnMount';
import { apiFetch } from '../../auth/apiFetch';
import './disposalRecon.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/* ── Splitter persistence ─────────────────────────────────────────────── */
const SPLIT_MIN = 0.25;
const SPLIT_MAX = 0.50;
const SPLIT_DEFAULT = 0.35;

function getSplitKey(email) {
  return `tmg_disposal_split_ratio_${email || 'default'}`;
}

function loadSplitRatio(email) {
  try {
    const v = parseFloat(localStorage.getItem(getSplitKey(email)));
    if (!isNaN(v) && v >= SPLIT_MIN && v <= SPLIT_MAX) return v;
  } catch { /* ignore */ }
  return SPLIT_DEFAULT;
}

function saveSplitRatio(email, ratio) {
  try { localStorage.setItem(getSplitKey(email), String(ratio)); } catch { /* ignore */ }
}

export default function DisposalReconPage() {
  const { callerEmail, userProfile } = useUser();
  const { resolveProject } = useProject();
  // UI gate only — get_deleted_tools / restore_tool enforce ADMIN themselves.
  const isAdmin = userProfile?.role === 'ADMIN';

  /* ── Grid mode state ──────────────────────────────────────────────── */
  const {
    activeMode, setActiveMode,
    columnVisibility, toggleColumn, showAllColumns, hideAllColumns,
    pendingChanges, setPendingChange, clearPendingChanges, isDirty,
    sortKeys, setSortKeys,
    savedViews, activeViewId, activeViewName, saveView, deleteView, loadView,
  } = useGridState(callerEmail, 'disposal', DISPOSAL_COLUMNS);

  /* ── Left-panel collapse (per-user, per-screen) ────────────────────── */
  const { collapsed, toggle } = useLeftPanelCollapse(callerEmail, 'disposal');

  /* ── Site selector state ───────────────────────────────────────────── */
  const [sites, setSites]             = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [sitesLoading, setSitesLoading] = useState(true);

  /* ── Tool list state ───────────────────────────────────────────────── */
  const [tools, setTools]             = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsError, setToolsError]   = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);

  /* ── Filter + search state ─────────────────────────────────────────── */
  const [filters, setFilters] = useState({
    listNumber: '', reconStatus: '', oem: '', workflowStatus: '', toolType: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  // Server-side canonical search (search_inventory) when query >= min length;
  // null = fall back to the full loaded `tools` list.
  const [searchResults, setSearchResults] = useState(null);
  const searchDebounceRef = useRef(null);

  /* ── Unsaved changes tracking ──────────────────────────────────────── */
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /* ── Resizable splitter state ──────────────────────────────────────── */
  const [splitRatio, setSplitRatio] = useState(() => loadSplitRatio(callerEmail));
  const draggingRef = useRef(false);
  const pageRef = useRef(null);
  const rightRef = useRef(null);

  // Reset scroll on entry (window = load-bearing; right pane = inner container)
  useScrollToTopOnMount(rightRef);

  useEffect(() => {
    setSplitRatio(loadSplitRatio(callerEmail));
  }, [callerEmail]);

  const handleSplitterMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(ev) {
      if (!draggingRef.current || !pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const ratio = (ev.clientX - rect.left) / rect.width;
      const clamped = Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, ratio));
      setSplitRatio(clamped);
    }

    function onMouseUp() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Save final ratio
        setSplitRatio(prev => { saveSplitRatio(callerEmail, prev); return prev; });
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [callerEmail]);

  /* ── Stats from loaded tools ───────────────────────────────────────── */
  const stats = useMemo(() => getDisposalStats(tools), [tools]);

  /* ── Reset filters when tools change ───────────────────────────────── */
  useEffect(() => {
    setFilters({ listNumber: '', reconStatus: '', oem: '', workflowStatus: '', toolType: '' });
    setSearchQuery('');
  }, [tools]);

  /* ── Load sites on mount ───────────────────────────────────────────── */
  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getSites(callerEmail, 'DISPOSAL')
      .then(list => {
        setSites(list || []);
      })
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, [callerEmail]);

  /* ── Auto-select when only one site is available ─────────────────────── */
  useEffect(() => {
    if (sites.length === 1 && !selectedSite) {
      setSelectedSite(sites[0].site_id);
    }
  }, [sites, selectedSite]);

  /* ── Load tools when site changes ──────────────────────────────────── */
  const loadTools = useCallback((keepSelection = false, selectToolId = null) => {
    if (!selectedSite || !callerEmail) { setTools([]); return; }
    setToolsLoading(true);
    setToolsError(null);
    if (!keepSelection) setSelectedTool(null);

    getDisposalTools(selectedSite, callerEmail)
      .then(list => {
        setTools(list || []);
        // selectToolId (handleToolDeleted): re-match by id → first row → none.
        if (selectToolId != null) {
          const rows = list || [];
          setSelectedTool(rows.find(t => t.id === selectToolId) ?? rows[0] ?? null);
          return;
        }
        if (!keepSelection && list && list.length > 0) setSelectedTool(list[0]);
      })
      .catch(err => {
        setToolsError(err.message);
        setTools([]);
      })
      .finally(() => setToolsLoading(false));
  }, [selectedSite, callerEmail]);

  useEffect(() => { loadTools(false); }, [loadTools]);

  /* ── Server-side canonical search (>= MIN_QUERY_LENGTH) ──────────────────
     Mirrors the S4/S5 reference: debounce 300ms → search_inventory (results
     transformed to camelCase). Shorter queries clear results so the full
     loaded list (shared client filter) shows instead. */
  useEffect(() => {
    const q = (searchQuery || '').trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!selectedSite || !callerEmail || q.length < MIN_QUERY_LENGTH) {
      setSearchResults(null);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDisposalReconTools(selectedSite, q, callerEmail)
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          // search_inventory doesn't compute the recon `completeness` object
          // (get_disposal_tools action=list does). Re-attach it from the
          // already-loaded full site list by id so the "Missing:" indicator
          // renders identically to the unfiltered list. Purely additive.
          const byId = new Map((tools || []).map(t => [t.id, t]));
          setSearchResults(list.map(r => {
            const loaded = byId.get(r.id);
            return loaded && loaded.completeness
              ? { ...r, completeness: loaded.completeness }
              : r;
          }));
        })
        .catch(() => setSearchResults([]));
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, selectedSite, callerEmail, tools]);

  /* ── Resolve project on site change (non-blocking) ─────────────────── */
  useEffect(() => {
    if (selectedSite) resolveProject(selectedSite);
  }, [selectedSite, resolveProject]);

  /* ── Handle tool selection ─────────────────────────────────────────── */
  function handleToolSelect(newTool) {
    if (newTool.id === selectedTool?.id) return;
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Discard them and switch tools?'
      );
      if (!confirmed) return;
    }
    setSelectedTool(newTool);
  }

  /* ── Handle tool deletion from detail panel ──────────────────────────── */
  // Moves selection WITHOUT handleToolSelect: its window.confirm would ask
  // whether to discard edits to a tool that no longer exists. Nor does it read
  // hasUnsavedChanges — the panel clears its own edits before calling this.
  // `tools` is read from the render closure and this is a plain
  // function, re-created every render, so it always sees the current
  // list. Wrapping this in useCallback without `tools` in the deps
  // would make the successor lookup silently stale.
  function handleToolDeleted(deletedToolId) {
    // pick the successor from the CURRENT list, before the refetch
    const idx = tools.findIndex(t => t.id === deletedToolId);
    const next = (idx >= 0 ? (tools[idx + 1] || tools[idx - 1]) : null) || null;
    loadTools(false, next ? next.id : null);
  }

  /* ── Recycle bin (ADMIN) ─────────────────────────────────────────────── */
  const [binOpen, setBinOpen] = useState(false);
  const closeBin = useCallback(() => setBinOpen(false), []);

  // A restored tool re-enters the list; selection must NOT move. keepSelection
  // (loadTools(true), as handleGridSave uses) neither clears the selected tool
  // nor auto-selects the first row, so whatever was selected — or nothing —
  // stays exactly as it was.
  const handleToolRestored = useCallback(() => {
    loadTools(true);
  }, [loadTools]);

  /* ── Handle tool update from detail panel ────────────────────────── */
  function handleToolUpdate(updatedTool) {
    // Update in tools array
    setTools(prev => prev.map(t => t.id === updatedTool.id ? { ...t, ...updatedTool } : t));
    // Update selected tool
    setSelectedTool(prev => prev && prev.id === updatedTool.id ? { ...prev, ...updatedTool } : prev);
  }

  /* ── Refresh completeness after PN add/delete ──────────────────────── */
  const handleRefreshCompleteness = useCallback(() => {
    if (!selectedSite || !callerEmail || !selectedTool) return;
    getDisposalTools(selectedSite, callerEmail)
      .then(list => {
        setTools(list || []);
        const refreshed = (list || []).find(t => t.id === selectedTool.id);
        if (refreshed) setSelectedTool(refreshed);
      })
      .catch(() => { /* silent — tools are still usable */ });
  }, [selectedSite, callerEmail, selectedTool]);

  /* ── Sorted tools for grid view ───────────────────────────────────── */
  const colTypeMap = useMemo(() => {
    const m = {};
    for (const c of DISPOSAL_COLUMNS) m[c.key] = c.type;
    return m;
  }, []);
  const sortedTools = useMemo(
    () => sortRecords(tools, sortKeys, colTypeMap),
    [tools, sortKeys, colTypeMap]
  );

  /* ── Grid batch save ───────────────────────────────────────────────── */
  const [gridSaving, setGridSaving] = useState(false);
  const [gridSaveError, setGridSaveError] = useState(null);

  const handleGridSave = useCallback(async (pendingChangesMap) => {
    const changes = [];
    for (const [toolId, fieldMap] of pendingChangesMap) {
      const tool_changes = {};
      for (const [camelKey, value] of Object.entries(fieldMap)) {
        const snakeKey = DISPOSAL_CAMEL_TO_SNAKE[camelKey] || camelKey;
        tool_changes[snakeKey] = value;
      }
      changes.push({ tool_id: toolId, tool_changes });
    }

    setGridSaving(true);
    setGridSaveError(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/batch_save_grid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          screen: 'disposal',
          caller_email: callerEmail,
          changes,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setGridSaveError(err.message || `Save failed (${response.status})`);
        return;
      }

      clearPendingChanges();
      setGridSaveError(null);
      loadTools(true);
    } catch (err) {
      setGridSaveError(err.message || 'Network error');
    } finally {
      setGridSaving(false);
    }
  }, [callerEmail, clearPendingChanges, loadTools]);

  const leftPct = `${(splitRatio * 100).toFixed(1)}%`;

  return (
    <div className="drc-page" ref={pageRef}>
      {/* ── Left pane ──────────────────────────────────────────────────── */}
      <div
        className={`drc-left${collapsed ? ' drc-left-collapsed' : ''}`}
        style={collapsed ? undefined : { width: leftPct, minWidth: '280px', maxWidth: 'none' }}
      >
        <div className="drc-left-header">
          <h2 className="drc-left-title">Disposal Recon</h2>
          <select
            className="drc-site-select"
            value={selectedSite}
            onChange={e => setSelectedSite(e.target.value)}
            disabled={sitesLoading}
          >
            <option value="">
              {sitesLoading ? 'Loading sites...' : '— Select a site —'}
            </option>
            {sites.map(s => (
              <option key={s.site_id} value={s.site_id}>
                {s.site_city
                  ? `${s.site_name} (${s.site_city})`
                  : s.supplier_site_code
                    ? `${s.site_name} (${s.supplier_site_code})`
                    : s.site_name}
              </option>
            ))}
          </select>
        </div>

        {/* ── Stat cards ─────────────────────────────────────────────── */}
        {selectedSite && !toolsLoading && tools.length > 0 && (
          <div className="drc-stats">
            <div className="drc-stat-card">
              <div className="drc-stat-value">{stats.total}</div>
              <div className="drc-stat-label">Total</div>
            </div>
            <div className="drc-stat-card">
              <div className="drc-stat-value open">{stats.open}</div>
              <div className="drc-stat-label">Open</div>
            </div>
            <div className="drc-stat-card">
              <div className="drc-stat-value closed">{stats.closed}</div>
              <div className="drc-stat-label">Closed</div>
            </div>
            <div className="drc-stat-card">
              <div className="drc-stat-value pct">{stats.pctComplete}%</div>
              <div className="drc-stat-label">Complete</div>
            </div>
          </div>
        )}

        {/* ── Tool list area ─────────────────────────────────────────── */}
        <div className="drc-tool-list">
          {!selectedSite && !toolsLoading && (
            <div className="drc-loading">Select a site above.</div>
          )}
          {toolsError && (
            <div className="drc-error">{toolsError}</div>
          )}
          {selectedSite && (
            <DisposalToolList
              tools={tools}
              searchResults={searchResults}
              selectedToolId={selectedTool?.id ?? null}
              onToolSelect={handleToolSelect}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filters={filters}
              onFilterChange={setFilters}
              loading={toolsLoading}
            />
          )}
        </div>
      </div>

      {/* ── Resizable splitter handle ──────────────────────────────────── */}
      {!collapsed && (
        <div
          className="drc-splitter"
          onMouseDown={handleSplitterMouseDown}
        />
      )}

      {/* ── Right pane ─────────────────────────────────────────────────── */}
      <div className="drc-right" ref={rightRef}>
        {/* Gate is (selectedSite || collapsed), not selectedSite alone.
            The site picker lives inside .drc-left and is display:none'd by
            .drc-left-collapsed (disposalRecon.css:932). collapsed persists to
            localStorage; selectedSite does not. Gating this toggle on
            selectedSite alone made the only un-collapse control unreachable
            once a multi-site user collapsed the pane — permanently, across
            sessions. Do not narrow this gate back. */}
        {(selectedSite || collapsed) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4) var(--space-6)', borderBottom: '1px solid var(--color-border)' }}>
            {/* Expand work area = collapse the left rail. */}
            <LeftPanelCollapseToggle collapsed={collapsed} onToggle={toggle} />
            {/* ONE grouped child, not three: the row is space-between, so it must
                keep exactly two flex children or it silently re-lays-out. With
                no site selected this renders nothing, exactly as before. */}
            {selectedSite && (
              <div className="drc-right-header-actions">
                <GridModeToggle activeMode={activeMode} onChange={setActiveMode} />
                {isAdmin && (
                  <button type="button" className="ddp-btn-cancel" onClick={() => setBinOpen(true)}>
                    Recycle bin
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {activeMode === 'grid' ? (
          <GridView
            records={tools}
            sortedRecords={sortedTools}
            sortKeys={sortKeys}
            setSortKeys={setSortKeys}
            columns={DISPOSAL_COLUMNS}
            screen="disposal"
            callerEmail={callerEmail}
            siteId={selectedSite}
            onSave={handleGridSave}
            onCancel={() => clearPendingChanges()}
            columnVisibility={columnVisibility}
            toggleColumn={toggleColumn}
            showAllColumns={showAllColumns}
            hideAllColumns={hideAllColumns}
            pendingChanges={pendingChanges}
            setPendingChange={setPendingChange}
            clearPendingChanges={clearPendingChanges}
            isDirty={isDirty}
            saving={gridSaving}
            saveError={gridSaveError}
            savedViews={savedViews}
            activeViewId={activeViewId}
            activeViewName={activeViewName}
            saveView={saveView}
            deleteView={deleteView}
            loadView={loadView}
          />
        ) : (
          <>
            {!selectedTool && (
              <div className="drc-right-empty">Select a tool to begin</div>
            )}
            {selectedTool && (
              <DisposalDetailPanel
                tool={selectedTool}
                onToolUpdate={handleToolUpdate}
                onRefreshCompleteness={handleRefreshCompleteness}
                onDirtyChange={setHasUnsavedChanges}
                onToolDeleted={handleToolDeleted}
              />
            )}
          </>
        )}
      </div>

      {/* Recycle bin — mounted as the LAST child of div.drc-page, deliberately NOT
          next to its button in the header row. ModalShell has no portal, so it
          paints inside whatever stacking context its mount point sits in; the
          same trap that keeps the delete confirm out of .ddp-sticky-top
          (position:sticky + z-index:20, below the topbar 50 and rail 100 — see
          DisposalDetailPanel.jsx:1131). No ancestor of div.drc-page up to main
          creates a stacking context (Build 3 Phase 0, 0.4). Do not move it next
          to its button. */}
      <DisposalRecycleBin
        isOpen={binOpen}
        onClose={closeBin}
        siteId={selectedSite}
        onRestored={handleToolRestored}
      />
    </div>
  );
}
